import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, catchError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { UserService } from 'app/core/user/user.service';
import {
  Profile,
  ProfilesResponse,
  AccountWithSubAccounts,
  ProfileWithNames,
  SwitchProfileResponse,
  PlatformAccount,
  PlatformAccountsResponse,
  PlatformSubAccount,
  PlatformSubAccountsResponse,
} from './profile.types';

const ACTIVE_PROFILE_KEY = 'active-profile-id';
const ACTIVE_SUBACCOUNT_NAME_KEY = 'active-subaccount-name';
const USER_DATA_KEY = 'user-data';
const AIVA_PRODUCT_KEYS = ['AIVA', 'AIVA_SUITE'];

interface NamedProfile {
  profile: Profile;
  accountName: string;
  subAccountName: string;
}

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _userService = inject(UserService);

  private _profilesGrouped = new BehaviorSubject<AccountWithSubAccounts[]>([]);
  private _activeProfileId = new BehaviorSubject<string | null>(null);
  private _activeSubAccountName = new BehaviorSubject<string | null>(null);
  private _isLoading = new BehaviorSubject<boolean>(false);

  get profilesGrouped$(): Observable<AccountWithSubAccounts[]> {
    return this._profilesGrouped.asObservable();
  }

  get activeProfileId$(): Observable<string | null> {
    return this._activeProfileId.asObservable();
  }

  get activeSubAccountName$(): Observable<string | null> {
    return this._activeSubAccountName.asObservable();
  }

  get isLoading$(): Observable<boolean> {
    return this._isLoading.asObservable();
  }

  constructor() {
    const storedProfileId = localStorage.getItem(ACTIVE_PROFILE_KEY);
    if (storedProfileId) {
      this._activeProfileId.next(storedProfileId);
    }
    const storedSubAccountName = localStorage.getItem(ACTIVE_SUBACCOUNT_NAME_KEY);
    if (storedSubAccountName) {
      this._activeSubAccountName.next(storedSubAccountName);
    }
  }

  getProfiles(): Observable<Profile[]> {
    return this._httpClient.get<ProfilesResponse>(`${environment.authenticationApiUrl}/profiles`).pipe(
      map(response => response.profiles.filter(p => !p.deleted)),
      tap((profiles) => {
        if (profiles.length > 0 && profiles[0].user) {
          const profileUser = profiles[0].user;
          const userData = {
            id: profileUser._id,
            name: profileUser.displayName,
            email: profileUser.email,
          };
          localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
          this._userService.user = userData;
        }
      })
    );
  }

  getAccounts(): Observable<PlatformAccount[]> {
    return this._httpClient.get<PlatformAccountsResponse>(`${environment.platformUrl}/accounts/public?size=500`).pipe(
      map(response => (response.content || []).filter(account => this._hasAivaProduct(account))),
      catchError(() => of([]))
    );
  }

  private _hasAivaProduct(account: PlatformAccount): boolean {
    return account.products?.some(product => AIVA_PRODUCT_KEYS.includes(product.key)) ?? false;
  }

  getSubAccountsByAccount(accountUID: string): Observable<PlatformSubAccount[]> {
    return this._httpClient
      .get<PlatformSubAccountsResponse>(`${environment.platformUrl}/accounts/${accountUID}/subaccounts/public?size=9999`)
      .pipe(
        map(response => response.content || []),
        catchError(() => of([]))
      );
  }

  switchProfile(profileId: string): Observable<SwitchProfileResponse> {
    return this._httpClient
      .post<SwitchProfileResponse>(`${environment.authenticationApiUrl}/auth/switch-profile/${profileId}`, {})
      .pipe(
        tap((response) => {
          localStorage.setItem('accessToken', response.accessToken);
          localStorage.setItem('refreshToken', response.refreshToken);
          localStorage.setItem(ACTIVE_PROFILE_KEY, profileId);
          this._activeProfileId.next(profileId);
        })
      );
  }

  loadProfilesGrouped(): Observable<AccountWithSubAccounts[]> {
    this._isLoading.next(true);

    const storedProfileId = localStorage.getItem(ACTIVE_PROFILE_KEY);
    if (storedProfileId && storedProfileId !== this._activeProfileId.getValue()) {
      this._activeProfileId.next(storedProfileId);
    }

    return this.getProfiles().pipe(
      switchMap((profiles) => {
        if (profiles.length === 0) {
          this._isLoading.next(false);
          return of([]);
        }

        if (environment.profileNamesFromAuthApi) {
          return of(this._groupProfiles(this._namedFromAuthApi(profiles)));
        }

        return forkJoin({
          profiles: of(profiles),
          accounts: this.getAccounts(),
        }).pipe(
          switchMap(({ profiles, accounts }) => {
            const uniqueAccountUIDs = [...new Set(profiles.map(p => p.accountUID))];

            const subAccountRequests = uniqueAccountUIDs.map(accountUID =>
              this.getSubAccountsByAccount(accountUID).pipe(map(subAccounts => ({ accountUID, subAccounts })))
            );

            return forkJoin(subAccountRequests).pipe(
              map((subAccountResults) => {
                return this._groupProfiles(this._namedFromPlatform(profiles, accounts, subAccountResults));
              })
            );
          })
        );
      }),
      tap((grouped) => {
        this._profilesGrouped.next(grouped);
        this._isLoading.next(false);
      })
    );
  }

  setActiveProfile(profileId: string): void {
    localStorage.setItem(ACTIVE_PROFILE_KEY, profileId);
    this._activeProfileId.next(profileId);
  }

  clearProfiles(): void {
    this._profilesGrouped.next([]);
    this._activeProfileId.next(null);
    this._activeSubAccountName.next(null);
    this._isLoading.next(false);
    localStorage.removeItem(ACTIVE_PROFILE_KEY);
    localStorage.removeItem(ACTIVE_SUBACCOUNT_NAME_KEY);
  }

  // Mirrors the platform path: an AIVA role stands in for the account's AIVA product,
  // and a missing account name means the account would not have been listed
  private _namedFromAuthApi(profiles: Profile[]): NamedProfile[] {
    return profiles
      .filter(profile => profile.account?.name && this._hasAivaRole(profile))
      .map((profile) => {
        const accountName = profile.account.name!;
        return { profile, accountName, subAccountName: profile.subaccount?.name || accountName };
      });
  }

  private _hasAivaRole(profile: Profile): boolean {
    return profile.roles?.some(role => AIVA_PRODUCT_KEYS.includes(role.product?.key)) ?? false;
  }

  private _namedFromPlatform(
    profiles: Profile[],
    accounts: PlatformAccount[],
    subAccountResults: { accountUID: string; subAccounts: PlatformSubAccount[] }[]
  ): NamedProfile[] {
    const accountsMap = new Map<string, PlatformAccount>();
    accounts.forEach((account) => {
      accountsMap.set(account.uuid, account);
    });

    const subAccountsMap = new Map<string, PlatformSubAccount[]>();
    subAccountResults.forEach(({ accountUID, subAccounts }) => {
      subAccountsMap.set(accountUID, subAccounts);
    });

    // Only accounts with an AIVA product come back from the accounts call
    return profiles
      .filter(profile => accountsMap.has(profile.accountUID))
      .map((profile) => {
        const accountName = accountsMap.get(profile.accountUID)!.name;
        const subAccounts = subAccountsMap.get(profile.accountUID) || [];
        const subAccount = subAccounts.find(sa => sa.uuid === profile.subAccountUID);
        return { profile, accountName, subAccountName: subAccount?.name || accountName };
      });
  }

  private _groupProfiles(namedProfiles: NamedProfile[]): AccountWithSubAccounts[] {
    const grouped = new Map<string, AccountWithSubAccounts>();
    const activeProfileId = this._activeProfileId.getValue();

    namedProfiles.forEach(({ profile, accountName, subAccountName }) => {
      const isActive = profile._id === activeProfileId;

      if (isActive) {
        localStorage.setItem(ACTIVE_SUBACCOUNT_NAME_KEY, subAccountName);
        this._activeSubAccountName.next(subAccountName);
      }

      const profileWithNames: ProfileWithNames = {
        profileId: profile._id,
        accountUID: profile.accountUID,
        subAccountUID: profile.subAccountUID,
        accountName,
        subAccountName,
        isActive,
      };

      if (!grouped.has(profile.accountUID)) {
        grouped.set(profile.accountUID, {
          account: { uuid: profile.accountUID, name: accountName },
          subAccounts: [],
          profiles: [],
        });
      }

      grouped.get(profile.accountUID)!.profiles.push(profileWithNames);
    });

    return Array.from(grouped.values());
  }
}
